import type { CurrencyCode, PaymentRequestModel } from "shared/types";
import { PaymentRequestAudienceTypes } from "shared/types";

export interface PaymentRequestAmountSummary {
	currency: CurrencyCode;
	totalAmount: number;
	collectedAmount: number;
	collectedOnStripeAmount: number;
	collectedExternallyAmount: number;
	remainingAmount: number;
	progress: number;
}

const relatedAmountAudienceTypes = [
	PaymentRequestAudienceTypes.SOCCER_TEAM_PLAYER,
	PaymentRequestAudienceTypes.SOCCER_CLUB_GENERIC
];

const shouldUseRelatedPaymentRequests = (paymentRequest: PaymentRequestModel) => {
	const firstRelatedPaymentRequest = paymentRequest.relatedPaymentRequests?.[0];
	const firstAudience = firstRelatedPaymentRequest?.audiences?.[0];

	return !!firstAudience && relatedAmountAudienceTypes.includes(firstAudience.type);
};

export const getPaymentRequestAmountSummaries = (
	paymentRequest: PaymentRequestModel
): PaymentRequestAmountSummary[] => {
	const paymentRequests = shouldUseRelatedPaymentRequests(paymentRequest)
		? paymentRequest.relatedPaymentRequests!
		: [paymentRequest];

	const summaryByCurrency = paymentRequests.reduce<Partial<Record<CurrencyCode, PaymentRequestAmountSummary>>>(
		(acc, item) => {
			const existingSummary = acc[item.currency] ?? {
				currency: item.currency,
				totalAmount: 0,
				collectedAmount: 0,
				collectedOnStripeAmount: 0,
				collectedExternallyAmount: 0,
				remainingAmount: 0,
				progress: 0
			};

			existingSummary.totalAmount += item.totalAmount ?? 0;
			existingSummary.collectedAmount += item.collectedAmount ?? 0;
			existingSummary.collectedOnStripeAmount += item.collectedStripe ?? 0;
			existingSummary.collectedExternallyAmount += item.collectedExternally ?? 0;

			acc[item.currency] = existingSummary;

			return acc;
		},
		{}
	);

	return Object.values(summaryByCurrency)
		.map(summary => {
			const collectedAmount =
				summary.collectedAmount || summary.collectedOnStripeAmount + summary.collectedExternallyAmount;
			const progress = Number(parseFloat(`${(collectedAmount / (summary.totalAmount || 1)) * 100}`).toFixed(2));

			return {
				...summary,
				collectedAmount,
				remainingAmount: summary.totalAmount - summary.collectedOnStripeAmount - summary.collectedExternallyAmount,
				progress
			};
		})
		.sort((a, b) => {
			if (a.currency === paymentRequest.currency) {
				return -1;
			}
			if (b.currency === paymentRequest.currency) {
				return 1;
			}

			return a.currency.localeCompare(b.currency);
		});
};
